import { Box, Button, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import themeColors from "../theme";
import HeaderBar from "../components/HeaderBar";
import { confirmCheckout } from "../api/subscriptionApi";
import { formatDate, formatTrialLength } from "../utils/subscription";

/**
 * Landing page after Stripe Checkout.
 *
 * Stripe's webhook is the authoritative update, but it can arrive a moment after
 * the browser redirect. This page asks the API to sync from the session id so
 * the user sees their real plan immediately instead of a stale empty state.
 */
export default function SubscribeSuccessPage() {
    const { teal, darkTeal, lightGray } = themeColors;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");

    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setError("Missing checkout session. If you completed payment, your plan will appear in account settings shortly.");
            setLoading(false);
            return;
        }

        confirmCheckout(sessionId)
            .then((data) => {
                setSubscription(data.subscription);
                if (!data.subscription?.active) {
                    setError("Your subscription is not active yet. Please check your billing status in account settings.");
                }
            })
            .catch((err) => {
                setError(
                    err.response?.data?.error ||
                    "We couldn't confirm your subscription just yet. It may take a moment to appear in your account."
                );
            })
            .finally(() => setLoading(false));
    }, [sessionId]);

    const trialEnd = formatDate(subscription?.trialEnd);
    const trialLength = formatTrialLength(subscription?.trialDaysGranted);

    return (
        <>
            <HeaderBar limitedNavigation />
            <Box sx={{ backgroundColor: lightGray, minHeight: "calc(100vh - 64px)", py: 5, px: 2 }}>
                <Box sx={{ maxWidth: 560, mx: "auto" }}>
                    <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                        {loading ? (
                            <>
                                <CircularProgress sx={{ color: teal, mb: 2 }} />
                                <Typography variant="body1" sx={{ color: "#5A6C7D" }}>
                                    Confirming your subscription...
                                </Typography>
                            </>
                        ) : (
                            <>
                                <CheckCircleOutlineIcon sx={{ color: teal, fontSize: 56, mb: 1 }} />
                                <Typography variant="h5" fontWeight={700} sx={{ color: "#1D3557", mb: 1 }}>
                                    {error ? "Subscription not yet confirmed" : "You're all set"}
                                </Typography>

                                {error ? (
                                    <Alert severity="info" sx={{ mt: 2, mb: 3, textAlign: "left" }}>{error}</Alert>
                                ) : (
                                    <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                                        {trialEnd
                                            ? `Your ${trialLength ? `${trialLength} ` : ""}free period runs until ${trialEnd}. We'll charge your card then unless you cancel before.`
                                            : "Your subscription is active."}
                                        {subscription?.promoCode && (
                                            <>
                                                <br />
                                                Code <strong>{subscription.promoCode}</strong> applied.
                                            </>
                                        )}
                                    </Typography>
                                )}

                                <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate("/dashboard")}
                                        sx={{ backgroundColor: teal, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: darkTeal } }}
                                    >
                                        Go to Dashboard
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate("/account")}
                                        sx={{ textTransform: "none", fontWeight: 600 }}
                                    >
                                        Account Settings
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Paper>
                </Box>
            </Box>
        </>
    );
}
